import React, { useEffect, useMemo, useState } from "react";

import { useAdminUserContext, useCreateVersionReview, useVersionReviews } from "utils";

import { Flex, Message, Modal, Option, Select, TextArea, Typography } from "components";

const SCORE_OPTIONS = Array.from({ length: 11 }, (_, i) => i);

type MultipleVersionReviewModalProps = {
  open: boolean;
  onClose: () => void;
  baselineVersion: string;
  candidateVersion: string;
};

export const MultipleVersionCommentModal = ({ open, onClose, baselineVersion, candidateVersion }: MultipleVersionReviewModalProps) => {
  const { user } = useAdminUserContext();
  const { data: versionReviews } = useVersionReviews();
  const { mutate: createReview, isPending: isCreateReviewPending } = useCreateVersionReview();

  const existingReview = useMemo(
    () =>
      versionReviews?.find(
        (versionReview) =>
          versionReview.reviewerId === user?.id && versionReview.baselineVersion === baselineVersion && versionReview.candidateVersion === candidateVersion
      ) ?? null,
    [versionReviews, user?.id, baselineVersion, candidateVersion]
  );
  const existingCurrent = existingReview?.revisions?.[0] ?? null;

  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!open) return;
    setScore(existingCurrent?.score ?? null);
    setComment(existingCurrent?.comment ?? "");
  }, [open, existingCurrent]);

  const handleSubmit = () => {
    if (score === null) return;
    createReview(
      { baselineVersion, candidateVersion, score, comment: comment || null },
      {
        onSuccess: () => {
          Message({
            content: existingReview
              ? `Review updated for ${baselineVersion} → ${candidateVersion}`
              : `Review submitted for ${baselineVersion} → ${candidateVersion}`,
            icon: "tick",
          });
          onClose();
        },
        onError: () => Message({ content: "Could not save the review. Please try again.", icon: "error" }),
      }
    );
  };

  return (
    <Modal
      title={existingReview ? "Update review" : "Review this comparison"}
      showModal={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={existingReview ? "Update review" : "Submit review"}
      okButtonProps={{ disabled: score === null || isCreateReviewPending, loading: isCreateReviewPending }}
      content={
        <Flex vertical gap="1rem" className="padding-100">
          <Typography variant="paragraph-m">
            {baselineVersion} (baseline) vs {candidateVersion} (candidate)
          </Typography>
          <Select label="Score (0 = unusable · 5 = acceptable with caveats · 10 = ship it)" value={score ?? undefined} onChange={setScore}>
            {SCORE_OPTIONS.map((scoreOption) => (
              <Option key={scoreOption} value={scoreOption}>
                {scoreOption}
              </Option>
            ))}
          </Select>
          <Typography variant="paragraph-s" color="grey-600"></Typography>
          <TextArea
            key={`${baselineVersion}:${candidateVersion}:${existingCurrent?.id ?? "new"}`}
            name="comment"
            label="Comment"
            placeholder="Why this score? Anything the silver-service team should adapt?"
            rows={4}
            initialValue={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Flex>
      }
    />
  );
};
